import { useState } from "react";
import { toast } from "sonner";
import { Clock, ShieldCheck, MessageCircle, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { waLink } from "@/config";

const SERVICE_OPTIONS = [
  "Passagens Aéreas",
  "Envio DHL Express",
  "Loja de Roupas",
  "Western Union / Câmbio",
  "Naturalização Brasileira",
  "Outro",
];

const inputCls =
  "w-full bg-white border border-navy/10 rounded-xl px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-gold/60 focus:border-gold transition-all";

export const QuoteSection = () => {
  const [form, setForm] = useState({
    servico: SERVICE_OPTIONS[0],
    nome: "",
    origem: "",
    destino: "",
    data: "",
    detalhes: "",
  });

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.error("Por favor, informe seu nome para montarmos sua mensagem.");
      return;
    }
    const lines = [
      `Olá, A.S.F Viagens! Meu nome é ${form.nome.trim()}.`,
      `Gostaria de uma cotação para: *${form.servico}*.`,
      form.origem && `Origem: ${form.origem}`,
      form.destino && `Destino: ${form.destino}`,
      form.data && `Data preferencial: ${form.data}`,
      form.detalhes && `Detalhes: ${form.detalhes}`,
    ].filter(Boolean);
    window.open(waLink(lines.join("\n")), "_blank");
    toast.success("Abrindo o WhatsApp com sua mensagem pronta!");
  };

  return (
    <section
      id="cotacao"
      data-testid="quote-section"
      className="relative bg-navy grain py-20 lg:py-32 overflow-hidden"
    >
      <div className="absolute -top-32 left-1/3 w-[36rem] h-[36rem] rounded-full bg-gold/10 blur-[130px] pointer-events-none" />

      <div className="relative z-10 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold mb-5">
            Cotador Inteligente
          </p>
          <h2
            data-testid="quote-title"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-snug text-ivory"
          >
            Receba sua cotação em minutos,{" "}
            <em className="italic text-gold">direto no WhatsApp.</em>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-ivory/60 leading-relaxed max-w-lg">
            Preencha os campos ao lado e sua mensagem chega pronta para nossa
            equipe. Sem cadastro, sem e-mail, sem espera.
          </p>
          <ul className="mt-10 space-y-5">
            {[
              [Clock, "Resposta em minutos durante o horário comercial"],
              [ShieldCheck, "Cotação gratuita e sem compromisso"],
              [MessageCircle, "Atendimento humano, em português"],
            ].map(([Icon, text]) => (
              <li key={text} className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-gold" />
                </span>
                <span className="text-ivory/75 text-sm sm:text-base">{text}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={handleSubmit}
            data-testid="quote-form"
            className="bg-ivory rounded-3xl p-6 sm:p-10 shadow-[0_40px_90px_rgba(0,0,0,0.45)]"
          >
            <div className="space-y-5">
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Tipo de serviço
                </label>
                <select
                  value={form.servico}
                  onChange={set("servico")}
                  data-testid="quote-service-select"
                  className={inputCls}
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Nome completo *
                </label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={set("nome")}
                  placeholder="Seu nome"
                  data-testid="quote-name-input"
                  className={inputCls}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Origem
                  </label>
                  <input
                    type="text"
                    value={form.origem}
                    onChange={set("origem")}
                    placeholder="São Paulo"
                    data-testid="quote-origin-input"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Destino
                  </label>
                  <input
                    type="text"
                    value={form.destino}
                    onChange={set("destino")}
                    placeholder="Lisboa"
                    data-testid="quote-destination-input"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Data preferencial
                </label>
                <input
                  type="date"
                  value={form.data}
                  onChange={set("data")}
                  data-testid="quote-date-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                  Observações / detalhes
                </label>
                <textarea
                  value={form.detalhes}
                  onChange={set("detalhes")}
                  rows={3}
                  placeholder="Ex.: 2 adultos e 1 criança, classe econômica..."
                  data-testid="quote-details-input"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="quote-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold px-8 py-4 rounded-full hover:bg-gold-light hover:-translate-y-0.5 transition-all duration-300 shadow-[0_12px_30px_rgba(212,175,55,0.35)]"
              >
                <Send className="w-4 h-4" />
                Enviar pelo WhatsApp
              </button>
              <p className="text-center text-xs text-slate-400">
                Ao enviar, o WhatsApp abre com a mensagem pronta. Nada é salvo no site.
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
